import React, { useEffect, useState, useCallback } from 'react';

const API = 'https://tm-audit-platform-production.up.railway.app';
// The only grantable internal roles. There is no "member" internal role —
// revoking access clears it entirely (the Remove button), it does not demote
// to a third tier.
const ROLES = ['admin', 'owner'];

// Admin-only Users/Admins page: see who has platform access, invite by email,
// and promote / demote / revoke / resend. Backed by the canonical
// user_profiles.internal_role + admin_invites (no hardcoded admin emails). All mutations are guarded
// server-side by requireAdmin; owner-only operations are additionally enforced
// by the backend and reflected here.
function AdminRoles({ session }) {
    const [admins, setAdmins] = useState([]);
    const [invites, setInvites] = useState([]);
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('admin');
    const [notice, setNotice] = useState(null);
    const [magicLink, setMagicLink] = useState(null);

    const getHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
    }), [session]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(API + '/api/admin/roles', { headers: getHeaders() });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to load');
            setAdmins(data.admins || []);
            setInvites(data.invites || []);
            setMe(data.me || null);
        } catch (e) {
            setNotice({ error: e.message });
        }
        setLoading(false);
    }, [getHeaders]);

    useEffect(() => { load(); }, [load]);

    const isOwner = me?.internal_role === 'owner';
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

    const call = async (label, fn) => {
        setBusy(label);
        setNotice(null);
        setMagicLink(null);
        try {
            const data = await fn();
            if (data?.magic_link) setMagicLink({ email: data.invite?.email || inviteEmail, link: data.magic_link });
            if (data?.status) setNotice({ ok: statusMessage(data.status, data) });
            await load();
        } catch (e) {
            setNotice({ error: e.message });
        }
        setBusy(null);
    };

    const post = async (path, body) => {
        const res = await fetch(API + path, {
            method: 'POST', headers: getHeaders(),
            body: body ? JSON.stringify(body) : undefined,
        });
        const data = await res.json();
        if (!res.ok || data.success === false) throw new Error(data.error || 'Request failed');
        return data;
    };
    const del = async (path) => {
        const res = await fetch(API + path, { method: 'DELETE', headers: getHeaders() });
        const data = await res.json();
        if (!res.ok || data.success === false) throw new Error(data.error || 'Request failed');
        return data;
    };

    const handleInvite = () => {
        if (!inviteEmail) return;
        call('invite', () => post('/api/admin/invites', { email: inviteEmail, role: inviteRole }))
            .then(() => setInviteEmail(''));
    };
    const handleSetRole = (m, role) => call('m-' + m.user_id, () => post(`/api/admin/roles/${m.user_id}`, { role }));
    const handleRemove = (m) => {
        if (!window.confirm(`Remove ${m.email} from internal admin access? (Their account/login is unaffected.)`)) return;
        call('m-' + m.user_id, () => del(`/api/admin/roles/${m.user_id}`));
    };
    const handleResend = (inv) => call('i-' + inv.id, () => post(`/api/admin/invites/${inv.id}/resend`));
    const handleRevoke = (inv) => call('i-' + inv.id, () => post(`/api/admin/invites/${inv.id}/revoke`));

    const pendingInvites = invites.filter(i => i.status === 'pending');
    const otherInvites = invites.filter(i => i.status !== 'pending');

    return (
        <div>
            {/* Invite form */}
            <div className="admin-table-wrap" style={{ marginBottom: 20 }}>
                <div style={{ padding: '14px 16px' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                        Invite an Admin or Member
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                            type="email"
                            className="admin-plan-select"
                            placeholder="email@example.com"
                            value={inviteEmail}
                            onChange={e => { setInviteEmail(e.target.value); setNotice(null); }}
                            style={{ flex: 1, minWidth: 220, padding: '6px 10px' }}
                            disabled={busy === 'invite'}
                        />
                        <select
                            className="admin-plan-select"
                            value={inviteRole}
                            onChange={e => setInviteRole(e.target.value)}
                            disabled={busy === 'invite'}
                        >
                            <option value="admin">Admin</option>
                            {isOwner && <option value="owner">Owner</option>}
                        </select>
                        <button className="history-back-link" onClick={handleInvite} disabled={busy === 'invite' || !inviteEmail}>
                            {busy === 'invite' ? 'Working…' : 'Send Invite'}
                        </button>
                    </div>
                    {notice?.error && <div style={{ color: '#e74c3c', fontSize: 13, marginTop: 8 }}>{notice.error}</div>}
                    {notice?.ok && <div style={{ color: 'var(--green)', fontSize: 13, marginTop: 8 }}>{notice.ok}</div>}
                    {magicLink && (
                        <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                                Send this sign-in link to {magicLink.email} manually:
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <input readOnly value={magicLink.link} className="admin-plan-select"
                                    style={{ flex: 1, fontFamily: 'var(--mono)', fontSize: 11, padding: '6px 8px' }}
                                    onFocus={e => e.target.select()} />
                                <button className="history-back-link" onClick={() => navigator.clipboard.writeText(magicLink.link)}>Copy</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {loading && <div className="history-loading"><div className="spinner" /></div>}

            {/* Current admins / members */}
            {!loading && (
                <div className="admin-table-wrap" style={{ marginBottom: 24 }}>
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Last Sign In</th>
                                <th>Status</th>
                                <th>Manage</th>
                            </tr>
                        </thead>
                        <tbody>
                            {admins.length === 0 && (
                                <tr><td colSpan={5} style={{ color: 'var(--text-muted)' }}>No platform members yet.</td></tr>
                            )}
                            {admins.map(m => {
                                const isSelf = me && (m.email || '').toLowerCase() === (me.email || '').toLowerCase();
                                const lockOwner = m.internal_role === 'owner' && !isOwner; // only owners manage owners
                                const busyKey = 'm-' + m.user_id;
                                return (
                                    <tr key={m.user_id}>
                                        <td>{m.email}{isSelf && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> (you)</span>}</td>
                                        <td>
                                            <select
                                                className="admin-plan-select"
                                                value={m.internal_role}
                                                disabled={busy === busyKey || lockOwner}
                                                onChange={e => handleSetRole(m, e.target.value)}
                                            >
                                                {ROLES.filter(r => r !== 'owner' || isOwner).map(r => (
                                                    <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>{fmtDate(m.last_sign_in)}</td>
                                        <td>
                                            <span style={{ fontSize: 11, color: m.has_logged_in ? 'var(--green)' : 'var(--text-muted)', fontFamily: 'var(--mono)', textTransform: 'uppercase' }}>
                                                {m.has_logged_in ? 'Active' : 'No sign-in yet'}
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                className="history-back-link"
                                                style={{ color: '#e74c3c' }}
                                                disabled={busy === busyKey || lockOwner}
                                                onClick={() => handleRemove(m)}
                                            >Remove</button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pending invites */}
            {!loading && (
                <>
                    <div style={{ fontWeight: 700, fontSize: 13, margin: '0 0 10px 2px', letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                        Pending Invites ({pendingInvites.length})
                    </div>
                    <div className="admin-table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Invited By</th>
                                    <th>Sent</th>
                                    <th>Expires</th>
                                    <th>Manage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pendingInvites.length === 0 && (
                                    <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>No pending invites.</td></tr>
                                )}
                                {pendingInvites.map(inv => (
                                    <tr key={inv.id}>
                                        <td>{inv.email}</td>
                                        <td style={{ textTransform: 'capitalize' }}>{inv.role}</td>
                                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{inv.invited_by_email || '—'}</td>
                                        <td>{fmtDate(inv.created_at)}</td>
                                        <td>{fmtDate(inv.expires_at)}</td>
                                        <td style={{ display: 'flex', gap: 8 }}>
                                            <button className="history-back-link" disabled={busy === 'i-' + inv.id} onClick={() => handleResend(inv)}>Resend</button>
                                            <button className="history-back-link" style={{ color: '#e74c3c' }} disabled={busy === 'i-' + inv.id} onClick={() => handleRevoke(inv)}>Revoke</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {otherInvites.length > 0 && (
                        <>
                            <div style={{ fontWeight: 700, fontSize: 13, margin: '20px 0 10px 2px', letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                                Invite History
                            </div>
                            <div className="admin-table-wrap">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Email</th>
                                            <th>Role</th>
                                            <th>Status</th>
                                            <th>Sent</th>
                                            <th>Resolved</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {otherInvites.map(inv => (
                                            <tr key={inv.id}>
                                                <td>{inv.email}</td>
                                                <td style={{ textTransform: 'capitalize' }}>{inv.role}</td>
                                                <td>
                                                    <span style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', color: inv.status === 'accepted' ? 'var(--green)' : inv.status === 'revoked' ? '#e74c3c' : 'var(--text-muted)' }}>
                                                        {inv.status}
                                                    </span>
                                                </td>
                                                <td>{fmtDate(inv.created_at)}</td>
                                                <td>{fmtDate(inv.accepted_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

function statusMessage(status, data) {
    switch (status) {
        case 'invited': return 'Invite created. Share the sign-in link below.';
        case 'promoted': return 'Existing user promoted — role assigned immediately.';
        case 'updated': return 'Role updated.';
        case 'unchanged': return 'That user already has this role.';
        case 'duplicate': return 'A pending invite already existed — it was refreshed.';
        case 'resent': return 'Invite re-sent. Share the new link below.';
        case 'revoked': return 'Invite revoked.';
        case 'removed': return 'Internal admin access removed.';
        default: return 'Done.';
    }
}

export default AdminRoles;
